/**
 * Overnight / cross-midnight shift helpers.
 *
 * Storage convention (dp_staff_rota): one row on the start calendar day with
 * shift_end TIME numerically earlier than shift_start (e.g. 18:00 / 02:00).
 * UI end values may use a "+1" suffix (e.g. "02:00+1") so managers explicitly
 * pick next-day ends (+1ημέρα) instead of relying on silent inference.
 */

export const NEXT_DAY_END_HOURS = [0, 1, 2, 3, 4, 5];
export const NEXT_DAY_SUFFIX = "+1";

export function formatHhmm(t, fallback = "09:00") {
  if (!t && t !== 0) return fallback;
  const raw = String(t).replace(/\+1$/, "");
  const parts = raw.split(":");
  const h = String(parts[0] || "09").padStart(2, "0");
  const m = String(parts[1] || "00").slice(0, 2).padStart(2, "0");
  return `${h}:${m}`;
}

export function shiftTimeToMins(t) {
  if (t == null || t === "") return null;
  const cleaned = String(t).replace(/\+1$/, "");
  const [hs, ms] = cleaned.split(":");
  const h = Number(hs);
  const m = Number(ms);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

export function toHourFloat(t) {
  if (t == null || t === "") return 0;
  const cleaned = String(t).replace(/\+1$/, "");
  const parts = cleaned.split(":");
  const h = parseInt(parts[0], 10) || 0;
  const m = parseInt(parts[1], 10) || 0;
  return h + m / 60;
}

/** Parse UI end (or start) value; start never has nextDay in normal use. */
export function parseShiftEndValue(val, fallback = "09:00") {
  const raw = String(val || "").trim();
  const nextDay = raw.endsWith(NEXT_DAY_SUFFIX);
  const timePart = nextDay ? raw.slice(0, -NEXT_DAY_SUFFIX.length) : raw;
  const time = formatHhmm(timePart || fallback, fallback);
  const mins = shiftTimeToMins(time);
  return { time, nextDay, mins };
}

export function formatShiftEndValue(time, nextDay) {
  const t = formatHhmm(time);
  return nextDay ? `${t}${NEXT_DAY_SUFFIX}` : t;
}

/**
 * Same-day: end must be strictly after start.
 * Explicit next-day end (+1): valid when end hour is in NEXT_DAY_END_HOURS.
 * Plain end < start without +1: invalid (ops must pick +1ημέρα).
 */
export function isValidShiftRange(startVal, endVal) {
  const start = parseShiftEndValue(startVal);
  const end = parseShiftEndValue(endVal);
  if (start.mins == null || end.mins == null) return false;
  if (end.nextDay) {
    const endHour = Math.floor(end.mins / 60);
    return NEXT_DAY_END_HOURS.includes(endHour);
  }
  return end.mins > start.mins;
}

/** Persist / cell value: always "HH:MM-HH:MM" (strip +1). */
export function serializeShiftRange(startVal, endVal) {
  const start = parseShiftEndValue(startVal).time;
  const end = parseShiftEndValue(endVal).time;
  return `${start}-${end}`;
}

/** When loading a stored overnight range into the custom picker, attach +1 to end. */
export function endValueForPicker(startHhmm, endHhmm) {
  const s = shiftTimeToMins(startHhmm);
  const e = shiftTimeToMins(endHhmm);
  if (s == null || e == null) return formatHhmm(endHhmm);
  if (e <= s) return formatShiftEndValue(endHhmm, true);
  return formatHhmm(endHhmm);
}

/** Duration in hours; wraps past midnight when end <= start. */
export function hoursBetweenHhmm(start, end) {
  const sh = toHourFloat(start);
  const eh = toHourFloat(end);
  let diff = eh - sh;
  if (diff <= 0) diff += 24;
  return diff;
}

/** End as hour float on a continuous timeline (end += 24 when overnight). */
export function intervalEndHourFloat(start, end) {
  const sh = toHourFloat(start);
  let eh = toHourFloat(end);
  if (eh <= sh) eh += 24;
  return eh;
}

export function isOvernightHhmm(start, end) {
  const sh = toHourFloat(start);
  const eh = toHourFloat(end);
  return eh <= sh;
}

/**
 * Day-column bar geometry: overnight bars run from start to the visible day end
 * (timeline/close), not zero-width.
 */
export function timelineBarGeometry(start, end, tStart, tEnd) {
  const clamp = (v, a, b) => Math.min(Math.max(v, a), b);
  const span = Math.max(1e-6, tEnd - tStart);
  const sh = toHourFloat(start);
  let eh = toHourFloat(end);
  if (eh <= sh) eh = tEnd;
  const startHour = clamp(sh, tStart, tEnd);
  const endHour = clamp(eh, tStart, tEnd);
  const leftPct = ((startHour - tStart) / span) * 100;
  const widthPct = ((Math.max(endHour - startHour, 0)) / span) * 100;
  return {
    leftPct: `${leftPct}%`,
    widthPct: `${widthPct}%`,
    visible: endHour > startHour,
    startHour,
    endHour,
  };
}

export function addCalendarDaysISO(isoDate, days) {
  const d = new Date(`${String(isoDate).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return String(isoDate).slice(0, 10);
  d.setDate(d.getDate() + days);
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${day}`;
}

/** SoftOne WACT: bump end_date when to_hour is numerically before/equal from_hour. */
export function softoneEndDateForShift(date, fromHour, toHour) {
  const base = String(date || "").slice(0, 10);
  if (!base || !fromHour || !toHour) return base;
  if (toHourFloat(toHour) <= toHourFloat(fromHour)) {
    return addCalendarDaysISO(base, 1);
  }
  return base;
}

export function nextDayEndHourLabel(hour, lang = "en") {
  const h = String(hour).padStart(2, "0");
  return lang === "gr" ? `${h} (+1ημέρα)` : `${h} (+1 day)`;
}
